import type { DraftAnswerOption, DraftEnableWhen, DraftMemberReference, DraftNode, DraftQuestion } from "./types";

export interface ConditionTarget {
  reference: DraftMemberReference;
  label: string;
  question: DraftQuestion;
}

export function temporaryOrPersisted(member: { id?: string; clientId: string }): DraftMemberReference {
  return member.id ? { kind: "Persisted", id: member.id } : { kind: "Temporary", clientRef: member.clientId };
}

export function referenceKey(reference: DraftMemberReference): string {
  return reference.kind === "Persisted" ? `persisted:${reference.id}` : `temporary:${reference.clientRef}`;
}

export function conditionTargets(nodes: readonly DraftNode[]): ConditionTarget[] {
  const targets: ConditionTarget[] = [];
  for (const node of nodes) {
    if (node.kind === "Question") {
      targets.push({ reference: temporaryOrPersisted(node), label: node.label, question: node });
      continue;
    }
    if (node.kind === "Section")
      targets.push(...conditionTargets(node.items.filter((item) => item.kind !== "RepeatingGroup")));
  }
  return targets;
}

export function defaultCondition(target: ConditionTarget): DraftEnableWhen {
  return {
    question: target.reference,
    operator: "=",
    value: defaultValue(target.question),
  };
}

export function optionReference(option: DraftAnswerOption): DraftMemberReference {
  return temporaryOrPersisted(option);
}

function defaultValue(question: DraftQuestion): Exclude<DraftEnableWhen, { fact: string }>["value"] {
  const value = question.value;
  if (value.kind === "Choice") {
    const option = value.options[0];
    return option ? { kind: "Choice", option: optionReference(option) } : { kind: "Text", value: "" };
  }
  if (value.kind === "Boolean") return { kind: "Boolean", value: false };
  if (value.kind === "Integer") return { kind: "Integer", value: 0 };
  if (value.kind === "Quantity") return { kind: "Quantity", value: 0 };
  if (value.kind === "Date") return { kind: "Date", value: "" };
  return { kind: "Text", value: "" };
}
